import { useEffect, useState } from 'react';

/**
 * Routing na fragmencie adresu (`#/urzadzenia/…`).
 *
 * Świadomie bez biblioteki i bez History API: aplikacja ma działać jako PWA
 * uruchamiana z ekranu telefonu, także wtedy, gdy serwer jest nieosiągalny.
 * Ścieżki w hashu nie wymagają ani przepisywania adresów po stronie serwera,
 * ani nawigacji sieciowej przy każdym przejściu (CLAUDE.md §6).
 */

export const START = '/urzadzenia';

export interface Trasa {
  sciezka: string;
  /** Parametry zapytania, np. `#/urzadzenia?q=Szwalnia`. */
  parametry: URLSearchParams;
}

export function trasaZAdresu(): Trasa {
  const hash = window.location.hash.replace(/^#/, '') || START;
  const granica = hash.indexOf('?');

  return granica === -1
    ? { sciezka: hash, parametry: new URLSearchParams() }
    : {
        sciezka: hash.slice(0, granica),
        parametry: new URLSearchParams(hash.slice(granica + 1)),
      };
}

export function idz(sciezka: string): void {
  window.location.hash = sciezka;
}

/**
 * Adres kodowany na naklejce (docs/adr/0004-qr-i-numer-boczny.md).
 *
 * Telefon bez zainstalowanej aplikacji — klienta, nowego pracownika — po zeskanowaniu
 * ma trafić pod właściwą kartę, a nie zobaczyć ciąg znaków bez znaczenia. Adres jest
 * w hashu, więc nie wymaga przepisywania ścieżek po stronie serwera.
 */
export function adresSkanu(token: string): string {
  const { origin, pathname } = window.location;
  return `${origin}${pathname}#/skan/${token}`;
}

export function useTrasa(): Trasa {
  const [trasa, setTrasa] = useState(trasaZAdresu);

  useEffect(() => {
    const reaguj = (): void => setTrasa(trasaZAdresu());
    window.addEventListener('hashchange', reaguj);
    return () => window.removeEventListener('hashchange', reaguj);
  }, []);

  return trasa;
}

/**
 * Dopasowanie ścieżki do wzorca z jednym parametrem, np. `/urzadzenia/:id`.
 * Zwraca wartość parametru albo `null`, gdy ścieżka nie pasuje.
 */
export function dopasuj(sciezka: string, wzorzec: string): string | null {
  const czesciSciezki = sciezka.split('/').filter(Boolean);
  const czesciWzorca = wzorzec.split('/').filter(Boolean);

  if (czesciSciezki.length !== czesciWzorca.length) return null;

  let parametr: string | null = null;
  for (let i = 0; i < czesciWzorca.length; i += 1) {
    const oczekiwane = czesciWzorca[i]!;
    const rzeczywiste = czesciSciezki[i]!;

    if (oczekiwane.startsWith(':')) parametr = decodeURIComponent(rzeczywiste);
    else if (oczekiwane !== rzeczywiste) return null;
  }

  return parametr;
}
